import {
  CONNECTORS,
  marketplaceUrl as marketplaceAt,
  oneMcpUrl as oneMcpAt,
  projectMcpUrl as projectMcpAt,
  readOnlyMcpUrl,
  SIGN_IN_NOTE,
  type ConnectTarget,
} from '@cortex/ai-tools';
import { toast } from '@cortex/ui';
import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router';
import { connectedTools, EVERY_PROJECT, type ConnectedTool } from '../../identity/index.ts';
import { api, getAccessToken } from '../../platform/index.ts';
import { config } from '../../platform/index.ts';
import { unwrap } from '../../identity/index.ts';

/**
 * Where cortex's MCP lives (ADR 0030). The assistant serves the ONE MCP of a person at `/mcp` (every project they
 * can open) and a project's own at `/mcp/<org>/<project>` (OAuth 2.1, RFC 9728 discovery); `VITE_MCP_BASE_URL`
 * names the assistant's public origin, and empty means this UI's own origin (the dev server and a same-host
 * ingress route /mcp to it).
 */
export function mcpBaseUrl(): string {
  const base = config.VITE_MCP_BASE_URL.replace(/\/+$/, '');
  return base || (typeof window !== 'undefined' ? window.location.origin : '');
}

/** The one MCP: every project the signed-in person can open. */
export const oneMcpUrl = () => oneMcpAt(mcpBaseUrl());
/** One project's MCP: that project only. */
export const projectMcpUrl = (org: string, slug: string) => projectMcpAt(mcpBaseUrl(), org, slug);

/**
 * The Claude Code plugin marketplace this cortex serves (its URL and its name, read from the marketplace itself), or
 * null while unknown or where the assistant serves none: Claude Code's recipe then adds the MCP server alone.
 */
export function useMarketplace(): ConnectTarget['marketplace'] {
  const [m, setM] = React.useState<ConnectTarget['marketplace']>(null);
  React.useEffect(() => {
    let alive = true;
    const url = marketplaceAt(mcpBaseUrl());
    fetch(url)
      .then(async (r) => (r.ok ? ((await r.json()) as { name?: unknown }) : null))
      .then((j) => alive && typeof j?.name === 'string' && setM({ url, name: j.name }))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return m;
}

export function CopyButton({ value, testId }: { value: string; testId?: string }) {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };
  return (
    <button
      type="button"
      onClick={() => void onCopy()}
      disabled={!value}
      data-testid={testId}
      className="flex items-center gap-1.5 h-8 px-2.5 rounded-[5px] border border-page-divider text-xs cursor-pointer bg-card-bg text-page-text hover:border-nav-active transition-colors disabled:opacity-50 shrink-0"
    >
      {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

const CODE_LINE =
  'flex-1 min-w-0 text-xs font-mono bg-page-bg rounded-[5px] px-2.5 py-2 overflow-x-auto whitespace-nowrap';

/**
 * The organization of a project (its own MCP's address names it), from the caller's project list; null while
 * unknown or when the caller cannot open the project.
 */
function useOrgOf(slug: string | undefined) {
  const [org, setOrg] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!slug) return;
    let alive = true;
    unwrap(api.GET('/api/workspace/projects', {}))
      .then((r) => alive && setOrg(r.projects.find((p) => p.slug === slug)?.org.slug ?? null))
      .catch(() => alive && setOrg(null));
    return () => {
      alive = false;
    };
  }, [slug]);
  return org;
}

/**
 * What a tool's recipe connects to. Read-only is the address with `?read_only=true` (ADR 0030), added as a server:
 * the plugin carries the one MCP that may write, so it is offered only for that.
 */
export function connectTargetOf(o: {
  url: string;
  readOnly: boolean;
  marketplace: ConnectTarget['marketplace'];
}): ConnectTarget {
  return o.readOnly
    ? { mcp: readOnlyMcpUrl(o.url), marketplace: null }
    : { mcp: o.url, marketplace: o.url === oneMcpUrl() ? o.marketplace : null };
}

/** One numbered step of the card. */
function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="flex gap-3" data-testid={`mcp-step-${n}`}>
      <span
        aria-hidden
        className="mt-px flex items-center justify-center w-5 h-5 rounded-full bg-nav-active text-white text-[11px] font-semibold shrink-0"
      >
        {n}
      </span>
      <div className="flex flex-col gap-2 min-w-0 flex-1">
        <h3 className="m-0 text-sm font-medium text-page-text">{title}</h3>
        {children}
      </div>
    </section>
  );
}

/**
 * Connect an AI tool to cortex (ADR 0030) in three steps: what it may reach (every project or this one, and
 * read-only), which tool, what to paste. Whatever the tool, it acts as you, with your access in each project.
 */
export function ConnectAiTool({ slug, guideHref }: { slug?: string; guideHref?: string }) {
  const org = useOrgOf(slug);
  const [scope, setScope] = React.useState<'all' | 'project'>('all');
  const [readOnly, setReadOnly] = React.useState(false);
  const [toolId, setToolId] = React.useState(CONNECTORS[0]!.id);
  const tool = CONNECTORS.find((c) => c.id === toolId) ?? CONNECTORS[0]!;
  const marketplace = useMarketplace();
  const url = scope === 'project' && slug && org ? projectMcpUrl(org, slug) : oneMcpUrl();
  const target = connectTargetOf({ url, readOnly, marketplace });
  const code = tool.code(target);
  const alternative = tool.alternative(target);
  return (
    <div className="flex flex-col gap-5 text-sm" data-testid="connect-ai-tool">
      <p className="text-page-muted m-0">
        Use cortex from Claude Code, Codex, Cursor or any MCP tool. It works as you: it can do only what you
        can do.
      </p>

      <Step n={1} title="Choose access">
        {slug ? (
          <fieldset className="flex flex-wrap gap-x-5 gap-y-1.5 border-0 p-0 m-0" data-testid="mcp-scope">
            <legend className="sr-only">Which projects</legend>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mcp-scope"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
                data-testid="mcp-scope-all"
              />
              Every project you can open
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mcp-scope"
                checked={scope === 'project'}
                onChange={() => setScope('project')}
                disabled={!org}
                data-testid="mcp-scope-project"
              />
              Only <span className="font-mono">{slug}</span>
            </label>
          </fieldset>
        ) : null}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={readOnly}
            onChange={(e) => setReadOnly(e.target.checked)}
            data-testid="mcp-read-only"
          />
          <span>
            Read-only <span className="text-page-muted">— it can look, but never change anything</span>
          </span>
        </label>
      </Step>

      <Step n={2} title="Choose your tool">
        <div className="flex flex-wrap gap-1.5" role="tablist" data-testid="mcp-tools">
          {CONNECTORS.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={c.id === tool.id}
              onClick={() => setToolId(c.id)}
              data-testid={`mcp-tool-${c.id}`}
              className={`h-8 px-3 rounded-[5px] border bg-card-bg text-xs cursor-pointer transition-colors ${
                c.id === tool.id
                  ? 'border-nav-active bg-nav-active/10 text-nav-active font-medium'
                  : 'border-page-divider text-page-text hover:border-nav-active'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </Step>

      <Step n={3} title={tool.where(target)}>
        <div className="relative">
          <pre
            className="m-0 bg-page-bg text-page-text rounded-[5px] p-3 pr-24 text-xs font-mono overflow-x-auto whitespace-pre"
            data-testid="mcp-tool-code"
          >
            {code}
          </pre>
          <div className="absolute top-2 right-2">
            <CopyButton value={code} testId="mcp-tool-copy" />
          </div>
        </div>
        <ul
          className="m-0 p-0 list-none flex flex-col gap-1 text-xs text-page-muted"
          data-testid="mcp-tool-notes"
        >
          {[...tool.notes(target), SIGN_IN_NOTE].map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      </Step>

      <ConnectedTools />

      <div className="flex flex-col gap-2">
        {guideHref ? (
          <Link
            to={guideHref}
            className="text-xs text-nav-active hover:underline self-start"
            data-testid="mcp-guide-link"
          >
            Full guide for developers →
          </Link>
        ) : null}
        <details className="text-xs text-page-muted" data-testid="mcp-more">
          <summary className="cursor-pointer">More</summary>
          <div className="mt-2 flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="font-medium text-page-text">Address</span>
              <div className="flex items-center gap-2">
                <code className={CODE_LINE} data-testid="mcp-url">
                  {target.mcp}
                </code>
                <CopyButton value={target.mcp} testId="mcp-url-copy" />
              </div>
            </div>
            {alternative ? (
              <div className="flex flex-col gap-1" data-testid="mcp-tool-alternative">
                <span className="font-medium text-page-text">{alternative.label}</span>
                <div className="flex items-center gap-2">
                  <code className={CODE_LINE}>{alternative.code}</code>
                  <CopyButton value={alternative.code} testId="mcp-alternative-copy" />
                </div>
              </div>
            ) : null}
            <ul className="list-disc ml-5 m-0 space-y-1">
              <li>
                It changes projects on branches and pull requests; a merge runs only after you confirm it.
              </li>
              <li>
                Every project tool takes <code className="font-mono">project</code> (from{' '}
                <code className="font-mono">list_projects</code>) and{' '}
                <code className="font-mono">branch</code> (default main). On a project&apos;s own address the
                project is fixed.
              </li>
              <li>
                Tool groups: by default the Board, requirements and sources, tests, pull requests, previews
                and the fleet&apos;s sessions. Choose others with{' '}
                <code className="font-mono">?toolsets=board,docs</code> on the address.
              </li>
              <li>The connection is MCP Streamable HTTP with OAuth 2.1 sign-in.</li>
            </ul>
          </div>
        </details>
      </div>
    </div>
  );
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : 'not yet');

/** The tools you connected (your MCP sign-ins), each with what it reaches and a Disconnect. */
function ConnectedTools() {
  const [tools, setTools] = React.useState<ConnectedTool[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const load = React.useCallback(() => {
    connectedTools
      .list(getAccessToken() ?? '')
      .then((t) => {
        setTools(t);
        setError(null);
      })
      .catch((e: unknown) => setError((e as Error).message));
  }, []);
  React.useEffect(load, [load]);
  const disconnect = async (t: ConnectedTool) => {
    try {
      await connectedTools.disconnect(getAccessToken() ?? '', t.id);
      toast.success(`Disconnected ${t.client_name ?? 'the tool'}`);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  return (
    <div className="flex flex-col gap-1.5" data-testid="mcp-connected">
      <h3 className="m-0 text-sm font-medium text-page-text">Your connected tools</h3>
      {error ? (
        <p className="text-xs text-page-muted m-0">{error}</p>
      ) : !tools ? (
        <p className="text-xs text-page-muted m-0">Loading…</p>
      ) : !tools.length ? (
        <p className="text-xs text-page-muted m-0" data-testid="mcp-connected-none">
          None yet.
        </p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col divide-y divide-page-divider">
          {tools.map((t) => (
            <li
              key={t.id}
              className="flex items-center justify-between gap-3 py-2"
              data-testid="mcp-connected-tool"
            >
              <div className="min-w-0 text-xs">
                <div className="font-medium text-page-text truncate">{t.client_name ?? t.client_id}</div>
                <div className="text-page-muted">
                  {t.project === EVERY_PROJECT ? 'Every project you can open' : `Project ${t.project}`} ·{' '}
                  {t.scopes.includes('mcp:write') ? 'read and write' : 'read-only'} · connected{' '}
                  {when(t.created_at)} · last used {when(t.last_used_at)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void disconnect(t)}
                data-testid="mcp-disconnect"
                className="h-8 px-2.5 rounded-[5px] border border-page-divider text-xs cursor-pointer bg-card-bg text-page-text hover:border-nav-active shrink-0"
              >
                Disconnect
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
